import React, { useEffect, useId } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";

import FollowButton from "../Button/FollowButton/FollowButton";
import {
  fetchPeopleRecommendations,
  dismissRecommendation,
  markRecommendationViewed,
  sendFollowFeedback,
} from "../../redux/recommendations/recommendationsActions";
import {
  selectRecommendationItems,
  selectRecommendationsLoading,
  selectRecommendationsCursor,
  selectRecommendationsHasMore,
} from "../../redux/recommendations/recommendationsSelectors";

const SuggestedFollowsPanel = ({
  items,
  loading,
  cursor,
  hasMore,
  fetchPeopleRecommendations,
  dismissRecommendation,
  markRecommendationViewed,
  sendFollowFeedback,
  className = "",
}) => {
  useEffect(() => {
    if (!items.length) {
      fetchPeopleRecommendations({ limit: 10 });
    }
  }, [fetchPeopleRecommendations, items.length]);

  useEffect(() => {
    items.forEach((item) =>
      markRecommendationViewed(item.recId, item.user.userId)
    );
  }, [items, markRecommendationViewed]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchPeopleRecommendations({ cursor, limit: 10 });
    }
  };

  const renderReasons = (reasonHints) => {
    if (!reasonHints || reasonHints.length === 0) {
      return null;
    }

    return (
      <Stack
        component="ul"
        className="suggested-follows-panel__reasons"
        role="list"
        aria-live="polite"
      >
        {reasonHints.slice(0, 2).map((reason, idx) => (
          <li key={idx}>{reason}</li>
        ))}
      </Stack>
    );
  };

  const headingId = useId();
  const subheadingId = useId();
  const panelClassName = ["suggested-follows-panel", className]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (!items.length && !loading) {
    return null;
  }

  return (
    <section
      className={panelClassName}
      aria-labelledby={headingId}
      aria-describedby={subheadingId}
    >
      <header className="suggested-follows-panel__header">
        <Typography variant="h6" id={headingId}>
          People you may like to follow
        </Typography>
        <Typography variant="body2" color="textSecondary" id={subheadingId}>
          Why you&apos;re seeing this
        </Typography>
      </header>
      <Stack spacing={2}>
        {items.map((item) => {
          const followUsername = item.user.userName || item.user.handle;
          const displayHandle = item.user.handle || followUsername || "user";
          return (
            <Card className="suggested-follows-panel__card" key={item.recId}>
              <CardHeader
                avatar={
                  <Avatar
                    src={item.user.avatar}
                    alt={item.user.displayName || displayHandle}
                  >
                    {(item.user.displayName || displayHandle || "?")[0]}
                  </Avatar>
                }
                action={
                  <IconButton
                    aria-label={`Dismiss ${item.user.displayName || `@${displayHandle}`}`}
                    onClick={() =>
                      dismissRecommendation(item.recId, item.user.userId)
                    }
                    size="small"
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                title={item.user.displayName || followUsername || displayHandle}
                subheader={`@${displayHandle}`}
              />
              <CardContent>
                {item.user.sports && item.user.sports.length > 0 && (
                  <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
                    {item.user.sports.slice(0, 3).map((sport) => (
                      <Chip
                        key={`${item.recId}-${sport}`}
                        label={sport}
                        size="small"
                      />
                    ))}
                  </Stack>
                )}
                {renderReasons(item.reasonHints)}
                <div className="suggested-follows-panel__actions">
                  <FollowButton
                    userId={item.user.userId}
                    username={followUsername}
                    avatar={item.user.avatar}
                    following={false}
                    onFollowChange={(isFollowing) => {
                      if (isFollowing) {
                        sendFollowFeedback(item.recId, item.user.userId);
                      }
                    }}
                    style={{ minWidth: "120px" }}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      <div className="suggested-follows-panel__footer">
        {loading && (
          <CircularProgress size={20} className="suggested-follows-panel__loader" />
        )}
        {!loading && hasMore && (
          <Button size="small" onClick={handleLoadMore}>
            Show more
          </Button>
        )}
      </div>
    </section>
  );
};

const mapStateToProps = createStructuredSelector({
  items: selectRecommendationItems,
  loading: selectRecommendationsLoading,
  cursor: selectRecommendationsCursor,
  hasMore: selectRecommendationsHasMore,
});

const mapDispatchToProps = {
  fetchPeopleRecommendations,
  dismissRecommendation,
  markRecommendationViewed,
  sendFollowFeedback,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SuggestedFollowsPanel);
